// VoIPinger — Virtual Number pricing data
// DID: Direct Inward Dialing numbers (local / national / mobile per country)
// Toll Free + UIFN data populated later.
//
// Row schema:
//   country, code (ISO-2 for flag), local, national, mobile, twoWay, uc
//   nrc (setup/activation), mrc (monthly), setup (extra), inbound (/min)
//   poa = true means "Price on Application" (contact us)
//   limited keys ('mobile':'limited' etc.) reflect the original "**" markers.

// Convert ISO-2 country code to flag emoji (kept for legacy compatibility)
export const flag = (cc) => {
  if (!cc || cc.length !== 2) return '🏳️';
  return cc
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    .join('');
};

// Return a real SVG flag image URL by ISO-2 code (works on Windows / all OSes).
// Uses flagcdn.com which serves crisp SVG and PNG variants.
// Some "regional" codes (e.g. XK Kosovo) have no flag — fall back to a neutral globe.
export const flagUrl = (cc, size = 'w40') => {
  if (!cc || cc.length !== 2) return null;
  const code = cc.toLowerCase();
  // Special / unsupported by flagcdn — fall back to a generic globe icon
  if (code === 'xk') return 'https://flagcdn.com/w40/rs.png'; // Kosovo → use Serbia placeholder
  return `https://flagcdn.com/${size}/${code}.png`;
};

export const flagSrcSet = (cc) => {
  if (!cc || cc.length !== 2) return null;
  const code = cc.toLowerCase();
  if (code === 'xk') return null;
  return `https://flagcdn.com/w40/${code}.png 1x, https://flagcdn.com/w80/${code}.png 2x`;
};

const r = (country, code, opts = {}) => ({
  country,
  code,
  local: opts.local ?? false,
  national: opts.national ?? false,
  mobile: opts.mobile ?? false,
  twoWay: opts.twoWay ?? false,
  uc: opts.uc ?? false,
  nrc: opts.nrc ?? null,
  mrc: opts.mrc ?? null,
  setup: opts.setup ?? 0,
  inbound: opts.inbound ?? null,
  poa: opts.poa ?? false,
  variant: opts.variant ?? null
});

export const DID_PRICING = [
  r('Albania', 'AL', { local: true, twoWay: true, uc: true, nrc: 45, mrc: 45, inbound: 0.065 }),
  r('Angola', 'AO', { local: true, nrc: 30, mrc: 30, inbound: 0.005 }),
  r('Argentina', 'AR', { local: true, twoWay: true, uc: true, nrc: 1.95, mrc: 1.95, inbound: 0.005 }),
  r('Anguilla', 'AI', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Antigua', 'AG', { mobile: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Austria', 'AT', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Australia', 'AU', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Bahamas', 'BS', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Barbados', 'BB', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Belgium', 'BE', { local: true, mobile: 'limited', twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: 'Local + 2-Way' }),
  r('Belgium', 'BE', { national: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: 'National' }),
  r('Belgium', 'BE', { local: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'Local + UC' }),
  r('Belize', 'BZ', { local: true, twoWay: true, uc: true, nrc: 30, mrc: 30, inbound: 0.005 }),
  r('Benin', 'BJ', { mobile: true, nrc: 35, mrc: 35, inbound: 0.055 }),
  r('Bosnia & Herzegovina', 'BA', { local: true, twoWay: true, uc: true, nrc: 1.9, mrc: 1.9, inbound: 0.005 }),
  r('Botswana', 'BW', { national: 'limited', twoWay: 'limited', poa: true }),
  r('Brazil', 'BR', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.95, mrc: 1.95, inbound: 0.005, variant: 'Local' }),
  r('Brazil', 'BR', { national: true, nrc: 4.5, mrc: 4.5, inbound: 0.005, variant: 'National' }),
  r('British Virgin Islands', 'VG', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Bolivia', 'BO', { national: true, nrc: 13, mrc: 54, inbound: 0.005, variant: 'National' }),
  r('Bolivia', 'BO', { twoWay: true, uc: true, nrc: 30, mrc: 30, inbound: 0.005, variant: '2-Way + UC' }),
  r('Bonaire', 'BQ', { mobile: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Bulgaria', 'BG', { local: true, twoWay: true, nrc: 3, mrc: 3, inbound: 0.005, variant: 'Local + 2-Way' }),
  r('Bulgaria', 'BG', { local: true, uc: true, nrc: 13, mrc: 4, inbound: 0.005, variant: 'Local + UC' }),
  r('Burkina Faso', 'BF', { local: true, nrc: 99, mrc: 99, inbound: 0.2451 }),
  r('Cambodia', 'KH', { local: true, nrc: 81, mrc: 81, inbound: 0.125 }),
  r('Cameroon', 'CM', { mobile: true, nrc: 30, mrc: 30, inbound: 0.381 }),
  r('Cayman Islands', 'KY', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Canada', 'CA', { local: true, twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: 'Local + 2-Way' }),
  r('Canada', 'CA', { local: true, mobile: 'limited', uc: true, nrc: 11.25, mrc: 2, inbound: 0.005, variant: 'Local + UC' }),
  r('Chile', 'CL', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 8, mrc: 8, inbound: 0.005 }),
  r('China', 'CN', { local: true, twoWay: true, nrc: 19, mrc: 19, inbound: 0.041 }),
  r('Colombia', 'CO', { local: true, national: true, twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Costa Rica', 'CR', { local: true, twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.016, variant: 'Local' }),
  r('Costa Rica', 'CR', { national: true, twoWay: true, nrc: 5, mrc: 5, inbound: 0.005, variant: 'National + 2-Way' }),
  r('Costa Rica', 'CR', { national: true, uc: true, nrc: 15, mrc: 6, inbound: 0.005, variant: 'National + UC' }),
  r("Côte d'Ivoire", 'CI', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Croatia', 'HR', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Curaçao', 'CW', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Cyprus', 'CY', { local: true, national: true, twoWay: true, nrc: 5, mrc: 5, inbound: 0.005 }),
  r('Czech Republic', 'CZ', { local: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Denmark', 'DK', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Dominica', 'DM', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Dominican Republic', 'DO', { local: true, twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Ecuador', 'EC', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('El Salvador', 'SV', { local: true, twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Egypt', 'EG', { mobile: true, nrc: 9, mrc: 18, inbound: 0.181 }),
  r('Estonia', 'EE', { local: true, twoWay: true, nrc: 1.5, mrc: 1.5, inbound: 0.005, variant: 'Local + 2-Way' }),
  r('Estonia', 'EE', { local: true, uc: true, nrc: 3.5, mrc: 3.5, inbound: 0.005, variant: 'Local + UC' }),
  r('Estonia', 'EE', { national: true, mobile: 'limited', twoWay: true, nrc: 4, mrc: 4, inbound: 0.005, variant: 'National + 2-Way' }),
  r('Estonia', 'EE', { national: true, uc: true, nrc: 14, mrc: 5, inbound: 0.005, variant: 'National + UC' }),
  r('Eswatini', 'SZ', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Ethiopia', 'ET', { local: true, nrc: 195, mrc: 125.84, inbound: 0.201 }),
  r('Finland', 'FI', { local: true, national: true, mobile: 'limited', twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: '2-Way' }),
  r('Finland', 'FI', { local: true, national: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'UC' }),
  r('France', 'FR', { local: true, national: true, twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: '2-Way' }),
  r('France', 'FR', { local: true, national: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'UC' }),
  r('Georgia', 'GE', { local: true, twoWay: true, nrc: 7, mrc: 7, inbound: 0.005, variant: 'Local' }),
  r('Georgia', 'GE', { national: true, twoWay: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'National' }),
  r('Germany', 'DE', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Ghana', 'GH', { mobile: true, twoWay: 'limited', nrc: 15, mrc: 29, inbound: 0.101 }),
  r('Greece', 'GR', { local: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Grenada', 'GD', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.04 }),
  r('Guatemala', 'GT', { local: true, nrc: 18, mrc: 18, inbound: 0.035, variant: 'Local' }),
  r('Guatemala', 'GT', { national: true, twoWay: true, uc: true, nrc: 9, mrc: 9, inbound: 0.005, variant: 'National' }),
  r('Guinea', 'GN', { mobile: 'limited', poa: true }),
  r('Honduras', 'HN', { local: true, nrc: 24, mrc: 24, inbound: 0.005 }),
  r('Hong Kong', 'HK', { national: true, twoWay: true, uc: true, nrc: 5, mrc: 5, inbound: 0.005 }),
  r('Hungary', 'HU', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Iceland', 'IS', { local: true, twoWay: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Ireland', 'IE', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Indonesia', 'ID', { local: true, twoWay: true, nrc: 15, mrc: 15, inbound: 0.01 }),
  r('Israel', 'IL', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Italy', 'IT', { local: true, twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: '2-Way' }),
  r('Italy', 'IT', { local: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'UC' }),
  r('Ivory Coast', 'CI', { national: true, nrc: 68, mrc: 68, inbound: 0.181 }),
  r('Jamaica', 'JM', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Jordan', 'JO', { local: true, nrc: 195, mrc: 195, inbound: 0.065 }),
  r('Kenya', 'KE', { local: true, twoWay: true, nrc: 8, mrc: 8, inbound: 0.005 }),
  r('Kazakhstan', 'KZ', { local: 'limited', mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Kyrgyzstan', 'KG', { local: true, nrc: 28, mrc: 28, inbound: 0.037 }),
  r('Kosovo', 'XK', { local: true, nrc: 30, mrc: 49, inbound: 0.0591 }),
  r('Kuwait', 'KW', { national: true, nrc: 39, mrc: 69, inbound: 0.059 }),
  r('Latvia', 'LV', { local: true, twoWay: true, nrc: 1.5, mrc: 1.5, inbound: 0.005, variant: '2-Way' }),
  r('Latvia', 'LV', { local: true, uc: true, nrc: 3.5, mrc: 3.5, inbound: 0.005, variant: 'UC' }),
  r('Liberia', 'LR', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Lithuania', 'LT', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Luxembourg', 'LU', { national: true, twoWay: true, nrc: 1.5, mrc: 1.5, inbound: 0.005, variant: 'National' }),
  r('Luxembourg', 'LU', { uc: true, nrc: 3.5, mrc: 3.5, inbound: 0.005, variant: 'UC' }),
  r('Macao', 'MO', { mobile: true, nrc: 18, mrc: 72, inbound: 0.181 }),
  r('Macedonia', 'MK', { local: true, nrc: 20, mrc: 20, inbound: 0.021 }),
  r('Malaysia', 'MY', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 7.5, mrc: 7.5, inbound: 0.01 }),
  r('Malawi', 'MW', { mobile: true, twoWay: true, nrc: 19, mrc: 19, inbound: 0.17 }),
  r('Malta', 'MT', { national: true, twoWay: true, nrc: 2.9, mrc: 2.9, inbound: 0.005 }),
  r('Mauritius', 'MU', { mobile: true, twoWay: 'limited', nrc: 12.5, mrc: 12.5, inbound: 0.171, variant: 'Mobile' }),
  r('Mauritius', 'MU', { national: true, nrc: 35, mrc: 35, inbound: 0.3151, variant: 'National' }),
  r('Mexico', 'MX', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 3, mrc: 3, inbound: 0.005 }),
  r('Montenegro', 'ME', { local: true, twoWay: 'limited', uc: 'limited', nrc: 34, mrc: 39, inbound: 0.041 }),
  r('Montserrat', 'MS', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Morocco', 'MA', { local: true, nrc: 135, mrc: 29, inbound: 0.151 }),
  r('Namibia', 'NA', { national: 'limited', twoWay: 'limited', poa: true }),
  r('Myanmar', 'MM', { local: true, nrc: 275, mrc: 59, inbound: 0.411 }),
  r('Netherlands', 'NL', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('New Zealand', 'NZ', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Nicaragua', 'NI', { mobile: true, nrc: 30, mrc: 30, inbound: 0.005 }),
  r('Nigeria', 'NG', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 17.5, mrc: 17.5, inbound: 0.0251 }),
  r('North Macedonia', 'MK', { local: true, twoWay: true, nrc: 1.9, mrc: 1.9, inbound: 0.005 }),
  r('Norway', 'NO', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Pakistan', 'PK', { local: true, twoWay: 'limited', uc: 'limited', nrc: 64, mrc: 9, inbound: 0.0251 }),
  r('Panama', 'PA', { local: true, twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Paraguay', 'PY', { local: true, twoWay: true, nrc: 39, mrc: 39, inbound: 0.04 }),
  r('Peru', 'PE', { local: true, mobile: 'limited', twoWay: true, uc: true, nrc: 7, mrc: 7, inbound: 0.005 }),
  r('Poland', 'PL', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Philippines', 'PH', { local: true, national: true, twoWay: true, uc: 'limited', nrc: 19, mrc: 19, inbound: 0.181 }),
  r('Portugal', 'PT', { local: true, national: true, twoWay: true, uc: true, nrc: 1.95, mrc: 1.25, inbound: 0.005 }),
  r('Puerto Rico', 'PR', { local: true, mobile: 'limited', twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.351 }),
  r('Romania', 'RO', { local: 'limited', twoWay: 'limited', uc: 'limited', poa: true }),
  r('Saba', 'BQ', { mobile: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Serbia', 'RS', { local: true, twoWay: true, uc: true, nrc: 1.9, mrc: 1.9, inbound: 0.005 }),
  r('Seychelles', 'SC', { national: true, nrc: 65, mrc: 47, inbound: 0.461 }),
  r('Singapore', 'SG', { local: true, twoWay: true, uc: 'limited', nrc: 4, mrc: 4, inbound: 0.005 }),
  r('Slovakia', 'SK', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Slovenia', 'SI', { local: true, national: true, twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: '2-Way' }),
  r('Slovenia', 'SI', { local: true, national: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'UC' }),
  r('South Africa', 'ZA', { local: true, national: true, mobile: 'limited', twoWay: true, uc: 'limited', nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Spain', 'ES', { local: true, national: true, twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Sri Lanka', 'LK', { mobile: true, nrc: 190, mrc: 35, setup: 0.191, inbound: 0.221 }),
  r('Saint Kitts and Nevis', 'KN', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Saint Vincent and the Grenadines', 'VC', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('St. Maarten', 'SX', { mobile: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('St. Eustatius', 'BQ', { mobile: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Sudan', 'SD', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Swaziland', 'SZ', { mobile: 'limited', poa: true }),
  r('Sweden', 'SE', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('Switzerland', 'CH', { local: true, twoWay: true, nrc: 1.25, mrc: 1.25, inbound: 0.005, variant: '2-Way' }),
  r('Switzerland', 'CH', { local: true, uc: true, nrc: 3.25, mrc: 3.25, inbound: 0.005, variant: 'UC' }),
  r('Thailand', 'TH', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 9.5, mrc: 9.5, inbound: 0.005 }),
  r('Trinidad and Tobago', 'TT', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Tunisia', 'TN', { national: true, nrc: 68.25, mrc: 29.25, setup: 0.156, inbound: 0.351 }),
  r('Turks and Caicos', 'TC', { national: true, twoWay: true, nrc: 45, mrc: 45, inbound: 0.041 }),
  r('Uganda', 'UG', { national: true, twoWay: 'limited', nrc: 8, mrc: 8, inbound: 0.005 }),
  r('Ukraine', 'UA', { local: 'limited', mobile: 'limited', twoWay: 'limited', poa: true }),
  r('United Arab Emirates', 'AE', { national: true, nrc: 150, mrc: 150, inbound: 0.125 }),
  r('United Kingdom', 'GB', { local: true, national: true, mobile: 'limited', twoWay: true, uc: true, nrc: 1.25, mrc: 1.25, inbound: 0.005 }),
  r('United States', 'US', { local: true, mobile: 'limited', twoWay: 'limited', uc: 'limited', nrc: 1.25, mrc: 1.25, setup: 0.0024, inbound: 0.005 }),
  r('Uruguay', 'UY', { local: true, poa: true }),
  r('Uzbekistan', 'UZ', { local: true, twoWay: 'limited', nrc: 69, mrc: 69, inbound: 0.029 }),
  r('Venezuela', 'VE', { local: true, twoWay: true, uc: true, nrc: 16, mrc: 16, inbound: 0.005 }),
  r('Vietnam', 'VN', { local: true, twoWay: true, uc: 'limited', nrc: 36, mrc: 51, inbound: 0.101 }),
  r('Zambia', 'ZM', { mobile: 'limited', twoWay: 'limited', poa: true }),
  r('Zimbabwe', 'ZW', { local: true, twoWay: true, nrc: 19, mrc: 19, inbound: 0.051 })
];

// Toll Free numbers — country-specific freephone numbers (0800, 1-800, ITFS)
const tf = (country, code, opts = {}) => ({
  country,
  code,
  nrc: opts.nrc ?? null,
  mrc: opts.mrc ?? null,
  setup: opts.setup ?? 0,
  landline: opts.landline ?? null,
  mobile: opts.mobile ?? null,
  poa: opts.poa ?? false,
  variant: opts.variant ?? null
});

export const TOLLFREE_PRICING = [
  tf('Albania', 'AL', { nrc: 137, mrc: 113, landline: 0.234, mobile: 0.234 }),
  tf('Anguilla', 'AI', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Argentina', 'AR', { nrc: 8.5, mrc: 8.5, landline: 0.079, mobile: 0.079 }),
  tf('Australia', 'AU', { nrc: 10, mrc: 10, landline: 0.033, mobile: 0.033, variant: '1300' }),
  tf('Australia', 'AU', { nrc: 10, mrc: 10, landline: 0.033, mobile: 0.033, variant: '1800' }),
  tf('Austria', 'AT', { nrc: 5, mrc: 5, landline: 0.043, mobile: 0.109 }),
  tf('Bahamas', 'BS', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Barbados', 'BB', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Belgium', 'BE', { nrc: 7, mrc: 5.5, landline: 0.0542, mobile: 0.237 }),
  tf('Bonaire', 'BQ', { nrc: 15, mrc: 15, landline: null, mobile: 0.2 }),
  tf('Bosnia and Herzegovina', 'BA', { nrc: 26, mrc: 20, landline: 0.16, mobile: 0.16 }),
  tf('Brazil', 'BR', { nrc: 7.5, mrc: 7.5, landline: 0.02, mobile: 0.04 }),
  tf('British Virgin Islands', 'VG', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Bulgaria', 'BG', { nrc: 40, mrc: 30, landline: 0.08, mobile: 0.152 }),
  tf('Brunei', 'BN', { nrc: 11, mrc: 11, landline: 0.2659, mobile: 0.2659 }),
  tf('Cambodia', 'KH', { nrc: 49, mrc: 55, landline: 0.2604, mobile: 0.2604 }),
  tf('Canada', 'CA', { poa: true, variant: 'In process' }),
  tf('Cayman Islands', 'KY', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Chile', 'CL', { nrc: 5, mrc: 16, landline: 0.09, mobile: 0.09 }),
  tf('China', 'CN', { nrc: 15, mrc: 15, landline: 0.085, mobile: 0.085 }),
  tf('Colombia', 'CO', { nrc: 16, mrc: 11, landline: 0.0868, mobile: 0.0868 }),
  tf('Costa Rica', 'CR', { nrc: 9, mrc: 10, landline: 0.2168, mobile: 0.2168 }),
  tf('Croatia', 'HR', { nrc: 6, mrc: 6, landline: 0.145, mobile: 0.145 }),
  tf('Curaçao', 'CW', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Cyprus', 'CY', { nrc: 5, mrc: 10.75, landline: 0.0769, mobile: 0.0769 }),
  tf('Czech Republic', 'CZ', { nrc: 8, mrc: 8, landline: 0.035, mobile: 0.095 }),
  tf('Denmark', 'DK', { nrc: 3.5, mrc: 3.5, landline: 0.02, mobile: 0.165 }),
  tf('Dominica', 'DM', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Dominican Republic', 'DO', { poa: true, variant: 'In process' }),
  tf('Ecuador', 'EC', { nrc: 129, mrc: 79, landline: 0.2239, mobile: 0.2239 }),
  tf('Egypt', 'EG', { nrc: 225, mrc: 71, landline: 0.2593, mobile: 0.2593 }),
  tf('El Salvador', 'SV', { nrc: 14, mrc: 19, setup: 0.2275, landline: 0.2275, mobile: 0.2275 }),
  tf('Estonia', 'EE', { nrc: 167, mrc: 50, landline: 0.0616, mobile: 0.3865 }),
  tf('Ethiopia', 'ET', { nrc: 156, mrc: 99, landline: 0.2, mobile: 0.2 }),
  tf('Finland', 'FI', { nrc: 11.5, mrc: 11.5, landline: 0.148, mobile: 0.1565 }),
  tf('France', 'FR', { nrc: 5, mrc: 6.5, landline: 0.0473, mobile: 0.08 }),
  tf('French Guiana', 'GF', { nrc: 5, mrc: 11, landline: 0.174, mobile: 0.174 }),
  tf('Germany', 'DE', { nrc: 6.5, mrc: 6.5, landline: 0.015, mobile: 0.084 }),
  tf('Greece', 'GR', { nrc: 5, mrc: 5, landline: 0.028, mobile: 0.21 }),
  tf('Grenada', 'GD', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Guadeloupe', 'GP', { nrc: 5, mrc: 11, landline: 0.174, mobile: 0.174 }),
  tf('Hong Kong', 'HK', { nrc: 10, mrc: 17, landline: 0.0596, mobile: 0.0596 }),
  tf('Hungary', 'HU', { nrc: 5, mrc: 5, landline: 0.032, mobile: 0.049 }),
  tf('Iceland', 'IS', { nrc: 17, mrc: 29, landline: 0.1272, mobile: 0.1272 }),
  tf('India', 'IN', { nrc: 5, mrc: 20, landline: 0.153, mobile: 0.153 }),
  tf('Indonesia', 'ID', { nrc: 25, mrc: 20, landline: 0.165, mobile: 0.165 }),
  tf('Ireland', 'IE', { nrc: 5, mrc: 7, landline: 0.0603, mobile: 0.12 }),
  tf('Israel', 'IL', { nrc: 5, mrc: 5, landline: 0.035, mobile: 0.035 }),
  tf('Ivory Coast', 'CI', { nrc: 65, mrc: 65, landline: 0.255, mobile: 0.255 }),
  tf('Jamaica', 'JM', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Kenya', 'KE', { nrc: 37, mrc: 15, landline: 0.13, mobile: 0.13 }),
  tf('Jordan', 'JO', { nrc: 11, mrc: 23, landline: 0.2165, mobile: 0.2489 }),
  tf('Kazakhstan', 'KZ', { nrc: 50, mrc: 35, landline: 0.22, mobile: 0.22 }),
  tf('Latvia', 'LV', { nrc: 21, mrc: 21, landline: 0.191, mobile: 0.191 }),
  tf('Lithuania', 'LT', { nrc: 11.5, mrc: 11.5, landline: 0.03, mobile: 0.045 }),
  tf('Luxembourg', 'LU', { nrc: 9, mrc: 9, landline: 0.03, mobile: 0.085 }),
  tf('Macao', 'MO', { nrc: 15, mrc: 20.5, landline: 0.325, mobile: null }),
  tf('Malaysia', 'MY', { nrc: 11, mrc: 11, landline: 0.1177, mobile: 0.1177 }),
  tf('Martinique', 'MQ', { nrc: 5, mrc: 11, landline: 0.174, mobile: 0.174 }),
  tf('Mauritius', 'MU', { nrc: 253, mrc: 30, landline: 0.2713, mobile: 0.2713 }),
  tf('Mayotte', 'YT', { nrc: 5, mrc: 11, landline: 0.174, mobile: 0.174 }),
  tf('Mexico', 'MX', { nrc: 8.5, mrc: 8.5, landline: 0.035, mobile: 0.035 }),
  tf('Moldova', 'MD', { nrc: 200, mrc: 45, landline: 0.31, mobile: 0.31 }),
  tf('Monaco', 'MC', { nrc: 9.8, mrc: 9.8, landline: 0.06, mobile: 0.06 }),
  tf('Montserrat', 'MS', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Myanmar', 'MM', { nrc: 181, mrc: 38, landline: 0.3043, mobile: 0.3043 }),
  tf('Nepal', 'NP', { nrc: 132, mrc: 44, setup: 0.219, landline: 0.219, mobile: 0.219 }),
  tf('Netherlands', 'NL', { nrc: 85, mrc: 6, setup: 0.013, landline: 0.03, mobile: 0.175 }),
  tf('New Zealand', 'NZ', { poa: true, variant: 'In process' }),
  tf('Nigeria', 'NG', { poa: true, variant: 'Prefix 0700 — on request' }),
  tf('Nigeria', 'NG', { poa: true, variant: 'Prefix 0800 — on request' }),
  tf('North Macedonia', 'MK', { nrc: 12.5, mrc: 12.5, landline: 0.21, mobile: 0.31 }),
  tf('Norway', 'NO', { nrc: 5, mrc: 11, landline: 0.066, mobile: 0.066 }),
  tf('Oman', 'OM', { nrc: 475, mrc: 115, landline: 0.32, mobile: 0.32 }),
  tf('Pakistan', 'PK', { nrc: 14, mrc: 27, landline: 0.222, mobile: 0.222 }),
  tf('Papua New Guinea', 'PG', { nrc: 22, mrc: 22, landline: 0.5017, mobile: 0.5017 }),
  tf('Paraguay', 'PY', { nrc: 60, mrc: 66, landline: 0.2647, mobile: 0.2647 }),
  tf('Peru', 'PE', { poa: true, variant: 'In process' }),
  tf('Philippines', 'PH', { poa: true, variant: 'In process' }),
  tf('Poland', 'PL', { nrc: 10.75, mrc: 10.75, landline: 0.0585, mobile: 0.0875 }),
  tf('Portugal', 'PT', { nrc: 6, mrc: 6, landline: 0.04, mobile: 0.12 }),
  tf('Puerto Rico', 'PR', { nrc: 6, mrc: 6, landline: 0.0439, mobile: 0.0439 }),
  tf('Qatar', 'QA', { nrc: 125, mrc: 20, landline: 0.26, mobile: 0.26 }),
  tf('Reunion', 'RE', { nrc: 5, mrc: 11, landline: 0.2801, mobile: 0.2801 }),
  tf('Romania', 'RO', { nrc: 17.5, mrc: 9, landline: 0.03, mobile: 0.08 }),
  tf('Saint Kitts and Nevis', 'KN', { nrc: 11, mrc: 11, landline: 0.2056, mobile: 0.2056 }),
  tf('Saint Pierre and Miquelon', 'PM', { nrc: 5, mrc: 41, landline: 0.246, mobile: 0.246 }),
  tf('Saba', 'BQ', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Saint Lucia', 'LC', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Saint Vincent and the Grenadines', 'VC', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Saudi Arabia', 'SA', { nrc: 35, mrc: 35, landline: 0.14, mobile: 0.14 }),
  tf('Serbia', 'RS', { nrc: 26, mrc: 16, landline: 0.07, mobile: 0.13 }),
  tf('Singapore', 'SG', { nrc: 5, mrc: 20, landline: 0.0716, mobile: 0.0716 }),
  tf('Slovakia', 'SK', { nrc: 8.5, mrc: 8.5, landline: 0.062, mobile: 0.09 }),
  tf('Slovenia', 'SI', { poa: true, variant: 'In process' }),
  tf('South Africa', 'ZA', { nrc: 7.5, mrc: 7.5, landline: 0.05, mobile: 0.05 }),
  tf('South Korea', 'KR', { nrc: 5, mrc: 11, setup: 0.0464, landline: 0.1561, mobile: 0.1561 }),
  tf('Spain', 'ES', { nrc: 5, mrc: 5, landline: 0.02, mobile: 0.105 }),
  tf('Sri Lanka', 'LK', { nrc: 91, mrc: 74, landline: 0.267, mobile: 0.267 }),
  tf('St. Maarten', 'SX', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('St. Eustatius', 'BQ', { nrc: 15, mrc: 15, landline: null, mobile: 0.2 }),
  tf('Sweden', 'SE', { nrc: 5, mrc: 9, landline: 0.04, mobile: 0.312 }),
  tf('Switzerland', 'CH', { nrc: 20, mrc: 10, landline: 0.06, mobile: 0.07 }),
  tf('Taiwan', 'TW', { nrc: 5, mrc: 15, landline: 0.155, mobile: 0.1978 }),
  tf('Tanzania', 'TZ', { nrc: 27, mrc: 33, landline: 0.2604, mobile: 0.2604 }),
  tf('Thailand', 'TH', { nrc: 24, mrc: 24, landline: 0.16, mobile: 0.16 }),
  tf('Trinidad and Tobago', 'TT', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Turkey', 'TR', { nrc: 17, mrc: 28, landline: 0.09, mobile: 0.09 }),
  tf('Turks and Caicos', 'TC', { nrc: 15, mrc: 15, landline: 0.2, mobile: 0.2 }),
  tf('Uganda', 'UG', { nrc: 15, mrc: 28, landline: 0.14, mobile: 0.14 }),
  tf('Ukraine', 'UA', { nrc: 5, mrc: 14, landline: 0.2082, mobile: 0.2082 }),
  tf('United Arab Emirates', 'AE', { nrc: 100, mrc: 32.5, landline: 0.12, mobile: 0.12 }),
  tf('United Kingdom', 'GB', { nrc: 2.5, mrc: 2.5, landline: 0.025, mobile: 0.045 }),
  tf('United States', 'US', { nrc: 5, mrc: 5, landline: 0.012, mobile: 0.012 }),
  tf('Uruguay', 'UY', { nrc: 66, mrc: 11, landline: 0.1474, mobile: 0.1474 }),
  tf('Venezuela', 'VE', { nrc: 105, mrc: 26, landline: 0.085, mobile: 0.095 })
];

// UIFN — Universal International Freephone Numbers
const ui = (country, code, opts = {}) => ({
  country,
  code,
  nrc: opts.nrc ?? null,
  mrc: opts.mrc ?? null,
  setup: opts.setup ?? 0,
  landline: opts.landline ?? null,
  mobile: opts.mobile ?? null,
  payphone: opts.payphone ?? null,
  poa: opts.poa ?? false
});

export const UIFN_PRICING = [
  ui('Argentina', 'AR', { nrc: 17.4, mrc: 9.3, landline: 0.126 }),
  ui('Australia', 'AU', { nrc: 10, mrc: 9, landline: 0.07, mobile: 0.07, payphone: 0.07 }),
  ui('Austria', 'AT', { nrc: 10, mrc: 9, landline: 0.08, mobile: 0.16, payphone: 0.16 }),
  ui('Belgium', 'BE', { nrc: 10, mrc: 9, setup: 0.04, landline: 0.06, mobile: 0.22 }),
  ui('Brazil', 'BR', { nrc: 10, mrc: 9, landline: 0.09, mobile: 0.17 }),
  ui('Bulgaria', 'BG', { nrc: 110, mrc: 9, landline: 0.12, mobile: 0.12, payphone: 0.12 }),
  ui('Canada', 'CA', { nrc: 10, mrc: 9, landline: 0.07, payphone: 0.07 }),
  ui('China', 'CN', { nrc: 5, mrc: 12.57, landline: 0.24, mobile: 0.24 }),
  ui('Colombia', 'CO', { nrc: 10, mrc: 9, landline: 0.1, mobile: 0.1, payphone: 0.1 }),
  ui('Costa Rica', 'CR', { nrc: 10, mrc: 9, landline: 0.18, mobile: 0.18 }),
  ui('Croatia', 'HR', { nrc: 10, mrc: 9, landline: 0.16, mobile: 0.16, payphone: 0.16 }),
  ui('Cyprus', 'CY', { nrc: 10, mrc: 9, landline: 0.1, mobile: 0.1, payphone: 0.1 }),
  ui('Czech Republic', 'CZ', { nrc: 40, mrc: 9, landline: 0.07, mobile: 0.17, payphone: 0.07 }),
  ui('Denmark', 'DK', { nrc: 10, mrc: 9, landline: 0.07, mobile: 0.24, payphone: 0.24 }),
  ui('Estonia', 'EE', { nrc: 35, mrc: 29, landline: 0.07, mobile: 0.34, payphone: 0.07 }),
  ui('Finland', 'FI', { nrc: 63, mrc: 21, landline: 0.16, mobile: 0.16, payphone: 0.16 }),
  ui('France', 'FR', { nrc: 10, mrc: 9, landline: 0.07, mobile: 0.12, payphone: 0.12 }),
  ui('French Guiana', 'GF', { nrc: 10, mrc: 9, landline: 0.14, mobile: 0.14, payphone: 0.14 }),
  ui('Germany', 'DE', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.32, payphone: 0.15 }),
  ui('Greece', 'GR', { nrc: 10, mrc: 9, landline: 0.08, mobile: 0.22, payphone: 0.08 }),
  ui('Guadeloupe', 'GP', { nrc: 10, mrc: 9, landline: 0.14, mobile: 0.14, payphone: 0.14 }),
  ui('Hong Kong', 'HK', { nrc: 10, mrc: 14, landline: 0.07, mobile: 0.07, payphone: 0.07 }),
  ui('Hungary', 'HU', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.13, payphone: 0.06 }),
  ui('Iceland', 'IS', { nrc: 30, mrc: 9, landline: 0.08, mobile: 0.08, payphone: 0.08 }),
  ui('Israel', 'IL', { nrc: 10, mrc: 9, landline: 0.09, mobile: 0.09, payphone: 0.09 }),
  ui('Italy', 'IT', { nrc: 10, mrc: 9, landline: 0.07 }),
  ui('Japan', 'JP', { nrc: 10, mrc: 9, landline: 0.34, mobile: 0.34, payphone: 0.34 }),
  ui('Latvia', 'LV', { nrc: 10, mrc: 9, landline: 0.178 }),
  ui('Lithuania', 'LT', { nrc: 110, mrc: 29, landline: 0.12, mobile: 0.12, payphone: 0.12 }),
  ui('Luxembourg', 'LU', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.27, payphone: 0.06 }),
  ui('Macao', 'MO', { nrc: 10, mrc: 9, landline: 0.27 }),
  ui('Macedonia', 'MK', { nrc: 50, mrc: 24, landline: 0.18, mobile: 0.37 }),
  ui('Malaysia', 'MY', { nrc: 10, mrc: 9, landline: 0.1, mobile: 0.1 }),
  ui('Malta', 'MT', { nrc: 55, mrc: 14, landline: 0.1, mobile: 0.1 }),
  ui('Martinique', 'MQ', { nrc: 10, mrc: 9, landline: 0.14, mobile: 0.14, payphone: 0.14 }),
  ui('Mayotte', 'YT', { nrc: 10, mrc: 9, landline: 0.14, mobile: 0.14, payphone: 0.14 }),
  ui('Moldova', 'MD', { nrc: 110, mrc: 29, landline: 0.298, mobile: 0.298 }),
  ui('Monaco', 'MC', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.06 }),
  ui('Netherlands', 'NL', { nrc: 10, mrc: 9, landline: 0.07, mobile: 0.22, payphone: 0.22 }),
  ui('New Zealand', 'NZ', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.15, payphone: 0.15 }),
  ui('Norway', 'NO', { nrc: 65, mrc: 34, landline: 0.13, mobile: 0.13 }),
  ui('Peru', 'PE', { nrc: 95, mrc: 104, landline: 0.19, mobile: 0.19, payphone: 0.19 }),
  ui('Philippines', 'PH', { nrc: 10, mrc: 9, landline: 0.22, mobile: 0.27 }),
  ui('Poland', 'PL', { nrc: 10, mrc: 9, landline: 0.11, mobile: 0.11, payphone: 0.11 }),
  ui('Portugal', 'PT', { nrc: 10, mrc: 9, landline: 0.09, mobile: 0.25, payphone: 0.09 }),
  ui('Reunion', 'RE', { nrc: 10, mrc: 9, landline: 0.14, mobile: 0.14, payphone: 0.14 }),
  ui('Romania', 'RO', { nrc: 5, mrc: 14, landline: 0.085, mobile: 0.115 }),
  ui('Saint Pierre and Miquelon', 'PM', { nrc: 15, mrc: 19, landline: 0.19, mobile: 0.19, payphone: 0.19 }),
  ui('Singapore', 'SG', { nrc: 10, mrc: 9, landline: 0.07, mobile: 0.07, payphone: 0.07 }),
  ui('Slovakia', 'SK', { nrc: 25, mrc: 19, landline: 0.095, mobile: 0.25 }),
  ui('Slovenia', 'SI', { nrc: 10, mrc: 9, landline: 0.195, mobile: 0.195, payphone: 0.195 }),
  ui('South Africa', 'ZA', { nrc: 10, mrc: 9, landline: 0.13, mobile: 0.13, payphone: 0.13 }),
  ui('South Korea', 'KR', { nrc: 10, mrc: 9, landline: 0.13, mobile: 0.13, payphone: 0.13 }),
  ui('Spain', 'ES', { nrc: 10, mrc: 9, landline: 0.06, mobile: 0.12, payphone: 0.06 }),
  ui('Sweden', 'SE', { nrc: 70, mrc: 39, landline: 0.07, mobile: 0.16 }),
  ui('Switzerland', 'CH', { nrc: 10, mrc: 9, setup: 0.05, landline: 0.055, mobile: 0.08 }),
  ui('Taiwan', 'TW', { nrc: 5, mrc: 19, landline: 0.15, mobile: 0.193, payphone: 0.193 }),
  ui('Thailand', 'TH', { nrc: 10, mrc: 13, landline: 0.21, mobile: 0.21 }),
  ui('United Kingdom', 'GB', { nrc: 10, mrc: 9, landline: 0.09, mobile: 0.09, payphone: 0.09 }),
  ui('United States', 'US', { nrc: 10, mrc: 9, landline: 0.085 }),
  ui('Uruguay', 'UY', { nrc: 22.12, mrc: 9, landline: 0.13, mobile: 0.13, payphone: 0.13 })
];

// Capability legend
export const CAPABILITIES = [
  { key: 'local', label: 'Local', desc: 'Geographic local DID number' },
  { key: 'national', label: 'National', desc: 'Country-wide non-geographic number' },
  { key: 'mobile', label: 'Mobile', desc: 'Mobile-range DID number' },
  { key: 'twoWay', label: '2-Way', desc: 'Inbound and outbound calling on the same number' },
  { key: 'uc', label: 'UC', desc: 'Suitable for Microsoft Teams / UC platforms' }
];
